/**
 * @fileoverview Shared threshold constants used across the application
 *
 * This file centralizes all threshold-related magic numbers to improve code
 * maintainability and consistency. Thresholds are limits, boundaries, or
 * decision points used throughout the application.
 *
 * @module shared/constants/thresholds
 */

// ═══════════════════════════════════════════════════════════════
// UI RENDERING THRESHOLDS
// ═══════════════════════════════════════════════════════════════

/**
 * Number of items above which to enable virtual scrolling
 * Virtual scrolling improves performance for large lists by only
 * rendering visible items
 */
export const VIRTUAL_SCROLL_THRESHOLD = 100;

/**
 * Number of items to display per page in paginated views
 */
export const PAGINATION_PAGE_SIZE = 25;

/**
 * Number of items to show in dropdown menus before enabling search
 */
export const DROPDOWN_SEARCH_THRESHOLD = 10;

/**
 * Maximum number of items to display in preview lists
 */
export const PREVIEW_LIST_LIMIT = 5;

// ═══════════════════════════════════════════════════════════════
// ANTHROPIC API FILE UPLOAD LIMITS
// These are enforced on all QIS upload surfaces.
// ═══════════════════════════════════════════════════════════════

/** File extensions treated as images by the Anthropic API */
export const ANTHROPIC_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

/** Max size per document file (4.5 MB) */
export const ANTHROPIC_DOC_MAX_BYTES = 4.5 * 1024 * 1024;

/** Max size per image file (3.75 MB) */
export const ANTHROPIC_IMG_MAX_BYTES = 3.75 * 1024 * 1024;

/** Max number of document files per request */
export const ANTHROPIC_MAX_DOCS = 5;

/** Max number of image files per request */
export const ANTHROPIC_MAX_IMAGES = 20;

/** Max total payload size (20 MB) */
export const ANTHROPIC_MAX_TOTAL_BYTES = 20 * 1024 * 1024;

// ═══════════════════════════════════════════════════════════════
// FILE SIZE LIMITS
// ═══════════════════════════════════════════════════════════════

/**
 * Maximum file upload size in megabytes
 */
export const MAX_FILE_SIZE_MB = 10;

/**
 * Maximum file upload size in bytes
 */
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

/**
 * Maximum image file size in megabytes
 */
export const MAX_IMAGE_SIZE_MB = 5;

/**
 * Maximum document file size in megabytes
 */
export const MAX_DOCUMENT_SIZE_MB = 20;

// ═══════════════════════════════════════════════════════════════
// DATA PROCESSING THRESHOLDS
// ═══════════════════════════════════════════════════════════════

/**
 * Minimum characters before triggering incremental parsing
 * For streaming data processing
 */
export const INCREMENTAL_PARSE_THRESHOLD = 100;

/**
 * Maximum number of pending messages to queue
 * Prevents unbounded memory growth
 */
export const MAX_PENDING_MESSAGES = 50;

/**
 * Maximum number of cached items
 */
export const MAX_CACHE_ITEMS = 100;

/**
 * Maximum number of history items to keep
 */
export const MAX_HISTORY_ITEMS = 50;

// ═══════════════════════════════════════════════════════════════
// RETRY/ATTEMPT LIMITS
// ═══════════════════════════════════════════════════════════════

/**
 * Maximum number of reconnection attempts before giving up
 */
export const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Maximum number of retry attempts for failed requests
 */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * Maximum number of validation attempts
 */
export const MAX_VALIDATION_ATTEMPTS = 3;

// ═══════════════════════════════════════════════════════════════
// TEXT/STRING LIMITS
// ═══════════════════════════════════════════════════════════════

/**
 * Minimum project name length
 */
export const PROJECT_NAME_MIN_LENGTH = 3;

/**
 * Maximum project name length
 */
export const PROJECT_NAME_MAX_LENGTH = 50;

/**
 * Maximum description length
 */
export const DESCRIPTION_MAX_LENGTH = 150;

/**
 * Maximum comment length
 */
export const COMMENT_MAX_LENGTH = 500;

/**
 * Maximum message length
 */
export const MESSAGE_MAX_LENGTH = 5000;

/**
 * Maximum test suite name length
 */
export const TEST_SUITE_NAME_MAX_LENGTH = 50;

/**
 * Test suite name warning threshold
 */
export const TEST_SUITE_NAME_WARNING_LENGTH = 45;

/**
 * Maximum test step name length
 */
export const TEST_STEP_NAME_MAX_LENGTH = 100;

/**
 * Maximum title word count before truncation
 */
export const TITLE_MAX_WORD_COUNT = 20;

// ═══════════════════════════════════════════════════════════════
// UI DIMENSION THRESHOLDS
// ═══════════════════════════════════════════════════════════════

/**
 * Canvas size for high-resolution logo rendering (pixels)
 */
export const LOGO_CANVAS_SIZE = 400;

/**
 * Default canvas width for exports (pixels)
 */
export const CANVAS_DEFAULT_WIDTH = 800;

/**
 * Default canvas height for exports (pixels)
 */
export const CANVAS_DEFAULT_HEIGHT = 600;

/**
 * Canvas padding for exports (pixels)
 */
export const CANVAS_PADDING = 40;

// ═══════════════════════════════════════════════════════════════
// CHART/VISUALIZATION LIMITS
// ═══════════════════════════════════════════════════════════════

/**
 * Maximum number of data points to show in trend charts
 */
export const MAX_TREND_DATA_POINTS = 14;

/**
 * Number of top users to display in reports
 */
export const TOP_USERS_LIMIT = 10;

/**
 * Maximum number of chart series to display
 */
export const MAX_CHART_SERIES = 5;

// ═══════════════════════════════════════════════════════════════
// SEARCH/FILTER THRESHOLDS
// ═══════════════════════════════════════════════════════════════

/**
 * Minimum search query length before executing search
 */
export const MIN_SEARCH_LENGTH = 2;

/**
 * Maximum number of search results to display
 */
export const MAX_SEARCH_RESULTS = 50;

/**
 * Maximum number of filter options to show
 */
export const MAX_FILTER_OPTIONS = 20;

// ═══════════════════════════════════════════════════════════════
// NOTIFICATION LIMITS
// ═══════════════════════════════════════════════════════════════

/**
 * Maximum number of notifications to display simultaneously
 */
export const MAX_VISIBLE_NOTIFICATIONS = 3;

/**
 * Maximum number of notifications to keep in history
 */
export const MAX_NOTIFICATION_HISTORY = 100;

// ═══════════════════════════════════════════════════════════════
// PERFORMANCE BUDGETS
// ═══════════════════════════════════════════════════════════════

/**
 * Maximum allowed main thread blocking time (ms)
 */
export const MAX_BLOCKING_TIME_MS = 50;

/**
 * Maximum batch size for processing items
 */
export const MAX_BATCH_SIZE = 100;

/**
 * Number of items to process per frame to avoid blocking UI
 */
export const ITEMS_PER_FRAME = 10;

// ═══════════════════════════════════════════════════════════════
// STREAM BUFFER THRESHOLDS
// ═══════════════════════════════════════════════════════════════

/**
 * Minimum buffer size to consider for stream completion
 */
export const STREAM_MIN_BUFFER_SIZE = 50;

/**
 * Large buffer size threshold for faster completion
 */
export const STREAM_LARGE_BUFFER_SIZE = 2000;
